import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CloseIcon } from "vega-icons";

import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { getFullName } from "@vega-api/custom/utils/user";
import {
  getGetDealShortlistQueryKey,
  useUpdateDealShortlistItem,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  SearchUser,
  type SearchUserItem,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUser";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

import { useOpportunityOwnerSearch } from "./hooks/useOpportunityOwnerSearch";

/**
 * The minimal slice of a shortlist item the modal needs: the id it writes the
 * owner against, and the LP name it shows / lists in error messages. Both
 * `ProspectWithMemberCount` and a Track row (mapped to this shape) satisfy it.
 */
export type OpportunityOwnerTarget = {
  id: string;
  limitedPartner: { name: string };
};

export type OpportunityOwnerModalProps = {
  open: boolean;
  closeModal: () => void;
  dealId: string;
  selectedProspects: OpportunityOwnerTarget[];
  onSuccess?: () => void;
};

export const OpportunityOwnerModal = ({
  open,
  closeModal,
  dealId,
  selectedProspects,
  onSuccess,
}: OpportunityOwnerModalProps) => {
  const { user: currentUser } = useSession();
  const showToast = useToast();
  const queryClient = useQueryClient();
  const [selectedOwner, setSelectedOwner] = useState<CoinvestPerson | null>(
    null,
  );

  const { setSearchValue, searchOptions, isOpportunityOwnerSearchLoading } =
    useOpportunityOwnerSearch({ dealId });

  const { mutateAsync: updateDealShortlistItem, isPending: isSubmitting } =
    useUpdateDealShortlistItem();

  const handleSubmit = async () => {
    if (!selectedOwner) {
      return;
    }

    const listOfFailedProspectNames: string[] = [];
    await Promise.allSettled(
      selectedProspects.map(async (prospect) => {
        try {
          await updateDealShortlistItem({
            dealId,
            dealShortlistItemId: prospect.id,
            data: {
              relationship: selectedOwner,
            },
          });
        } catch (error) {
          listOfFailedProspectNames.push(prospect.limitedPartner.name);
          throw error;
        }
      }),
    );

    if (listOfFailedProspectNames.length > 0) {
      showToast({
        title: m.status_error(),
        description: m.coInvestments_addOpportunityOwner_error({
          prospectNames: listOfFailedProspectNames.join(", "),
        }),
        variant: "destructive",
      });
    }

    if (listOfFailedProspectNames.length < selectedProspects.length) {
      showToast({
        title: m.status_success(),
        description: m.coInvestments_addOpportunityOwner_success(),
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: getGetDealShortlistQueryKey(dealId),
      });
      onSuccess?.();
    }

    setSelectedOwner(null);
    closeModal();
  };

  const handleAssignToMe = () => {
    if (currentUser) {
      setSelectedOwner({
        id: currentUser.id,
        fullName: {
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
        },
        email: currentUser.email,
      });
    }
  };

  const handleUserSelect = (item: SearchUserItem | null) => {
    if (item) {
      setSelectedOwner({
        id: item.user.id,
        fullName: {
          firstName: item.user.firstName,
          lastName: item.user.lastName,
        },
        email: item.user.email,
      });
    }
  };

  const handleRemoveUser = () => {
    setSelectedOwner(null);
  };

  const prospectsNames = selectedProspects
    .map((prospect) => prospect.limitedPartner.name)
    .join(", ");

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionText: m.coInvestments_addOpportunityOwner_modal_addButton(),
        mainAction: handleSubmit,
        secondaryActionText: m.btn_cancel(),
        secondaryAction: closeModal,
        disabled: isSubmitting || !selectedOwner,
        disabledSecondary: isSubmitting,
      }}
      dataTestId="opportunity-owner-modal"
      description={m.coInvestments_addOpportunityOwner_modal_description()}
      fluidHeight
      open={open}
      setOpen={closeModal}
      title={m.coInvestments_addOpportunityOwner_modal_header()}
    >
      <div className="w-dvw max-w-xl text-start flex flex-col gap-4 items-start">
        <p className="text-text/primary body-semi-large-medium">
          {prospectsNames}
        </p>

        <SearchUser
          clearOnSelect
          isLoading={isOpportunityOwnerSearchLoading}
          name="opportunityOwner"
          noPadding
          onChange={setSearchValue}
          onUserSelect={handleUserSelect}
          users={searchOptions}
        />

        {selectedOwner ? (
          <div className="flex gap-3 items-center w-full">
            <Avatar initials={selectedOwner.fullName.firstName} />
            <div className="flex flex-col flex-1 min-w-0">
              <span className="body-base-medium text-text/primary truncate">
                {getFullName(selectedOwner.fullName)}
              </span>
              <span className="body-small-regular text-text/secondary truncate">
                {selectedOwner.email}
              </span>
            </div>
            <ThemedButton
              dataTestId="remove-user-button"
              icon={<CloseIcon className="w-4 h-4" />}
              onClick={handleRemoveUser}
              variant="text"
            />
          </div>
        ) : null}

        <ThemedButton
          className="body-small-regular text-text/secondary"
          dataTestId="assign-to-me-button"
          onClick={handleAssignToMe}
          variant="text"
        >
          {m.cases_assignToMe()}
        </ThemedButton>
      </div>
    </ActionaryModal>
  );
};
